import {
  Avatar,
  Button,
  CircularProgress,
  Tag,
  Typography,
} from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import { useCopyToClipboard } from "@uidotdev/usehooks"
import api from "api"
import clsx from "clsx"
import Navbar from "components/Navbar"
import PageContainer from "components/PageContainer"
import BackButton from "components/shared/BackButton"
import SubmissionContent from "features/Assignments/Check/SubmissionContent"
import { formatAssignmentTitle } from "features/Assignments/utils"
import { Attachment, Check, Copy, ViewGrid } from "iconoir-react"
import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { formatDate } from "utils/helpers"
import { useSkillEvidenceStyles } from "utils/hooks/useSkillEvidenceStyles"

import IQARubricSheet from "./IQARubricSheet"

const IQASubmissionPage = () => {
  const { id } = useParams() as { id: string }
  const styles = useSkillEvidenceStyles()

  const [showReview, setShowReview] = useState(false)

  const {
    isLoading,
    isError,
    error,
    data: submissionDetail,
  } = useQuery({
    queryKey: ["iqa-submission", id],
    queryFn: () =>
      api.submissions.retrieve({
        urlParams: { submissionId: id },
        params: {
          fields: [
            "review",
            "iqa_review",
            "review_rubrics",
            "courses",
            "iqa_review_rubrics",
            "submitted_at",
            "citations",
          ],
        },
      }),
  })

  if (isError) {
    // eslint-disable-next-line no-console
    console.error("[IQASubmissionPage] Failed to load submission:", error)
  }

  const [copiedText, copyToClipboard] = useCopyToClipboard()

  const copySubmissionToClipboard = () => {
    const text = document.querySelector("#submission-text")
    if (text) copyToClipboard(text.textContent || "")
  }

  const onFixMargins = () => {
    const richTextElements = document.querySelectorAll(".suraasa-se-rich-text")
    richTextElements.forEach(element => {
      element.classList.toggle("fix-negative-margins")
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar gutterBottom={false} hideBackButton />
        <PageContainer>
          <div className="mt-4 flex justify-center">
            <CircularProgress className="size-4" />
          </div>
        </PageContainer>
      </div>
    )
  }

  if (isError || !submissionDetail) {
    return (
      <div className="min-h-screen">
        <Navbar gutterBottom={false} hideBackButton />
        <PageContainer>
          <div className="mt-2 flex flex-col gap-2">
            <BackButton />
            <Typography className="text-critical-500">
              Failed to load submission. Check the browser console for details.
            </Typography>
          </div>
        </PageContainer>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-4">
      {submissionDetail && (
        <IQARubricSheet
          open={showReview}
          onClose={() => setShowReview(false)}
          submissionDetail={submissionDetail}
          queryKey={["iqa-submission", id]}
        />
      )}

      <Navbar gutterBottom={false} hideBackButton />

      <PageContainer>
        <div className="mt-2 flex flex-col gap-2">
          <BackButton />

          {submissionDetail && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-1">
                <div className="flex flex-wrap items-center gap-1">
                  <Avatar
                    name={submissionDetail.user.fullName}
                    className="size-5"
                  />
                  <div className="flex flex-col">
                    <Typography variant="title3">
                      {submissionDetail.user.fullName}
                    </Typography>
                    <Typography
                      variant="smallBody"
                      className="text-secondary-500"
                    >
                      {submissionDetail.user.email}
                    </Typography>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Tag>{submissionDetail.assignment.title}</Tag>
                    </div>
                  </div>
                </div>
                {submissionDetail.submittedAt && (
                  <Typography
                    variant="smallBody"
                    className="text-secondary-500"
                  >
                    Submitted on {formatDate(submissionDetail.submittedAt)}
                  </Typography>
                )}
              </div>

              <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                <Typography variant="title4">
                  {formatAssignmentTitle({
                    title: submissionDetail.assignment.title,
                    shortTitle: null,
                  })}
                </Typography>
                {submissionDetail.assignment.description && (
                  <div
                    className={clsx(styles.className, "break-words")}
                    dangerouslySetInnerHTML={{
                      __html: submissionDetail.assignment.description,
                    }}
                  />
                )}
              </div>

              <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                <div className="flex flex-wrap items-center gap-1">
                  <Typography variant="title4">
                    Assignment Submission
                  </Typography>
                  <Button
                    startAdornment={copiedText ? <Check /> : <Copy />}
                    variant="text"
                    size="sm"
                    onClick={copySubmissionToClipboard}
                    color={copiedText ? "success" : "primary"}
                    className="px-0.5 py-0.25"
                  >
                    {copiedText ? "Copied" : "Copy Submission"}
                  </Button>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={onFixMargins}
                    className="px-0.5 py-0.25"
                  >
                    Fix margins
                  </Button>
                </div>

                <SubmissionContent
                  submission={submissionDetail.submission}
                  submissionId={id}
                  viewMode="default"
                />

                {submissionDetail.attachments.length > 0 && (
                  <>
                    <hr />
                    <div>
                      {submissionDetail.attachments.map((a, i) => (
                        <div key={i} className="flex">
                          <Link to={a.file} target="_blank" className="flex">
                            <div className="flex items-center gap-1">
                              <div className="rounded border p-1">
                                <Attachment className="size-2.5" />
                              </div>
                              <Typography variant="strongSmallBody">
                                {a.name}
                              </Typography>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {submissionDetail.references && (
                <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                  <Typography variant="title4">References</Typography>
                  <div
                    className={clsx(styles.className, "break-words")}
                    dangerouslySetInnerHTML={{
                      __html: submissionDetail.references,
                    }}
                  />
                </div>
              )}

              {submissionDetail.citations && (
                <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                  <Typography variant="title4">Citations</Typography>
                  <div
                    className={clsx(styles.className, "break-words")}
                    dangerouslySetInnerHTML={{
                      __html: submissionDetail.citations,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </PageContainer>

      {submissionDetail && !showReview && (
        <div className="fixed bottom-2 right-2 cursor-pointer rounded-full">
          <Button
            startAdornment={<ViewGrid />}
            onClick={() => setShowReview(true)}
            size="lg"
            rounded
            className="shadow-lg"
          >
            View Review
          </Button>
        </div>
      )}
    </div>
  )
}

export default IQASubmissionPage
